import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { getHomeFeed, getTimeRemaining } from '../../api/linguaSnaps';
import LinguaSnapViewer from './LinguaSnapViewer';
import LinguaSnapUpload from './LinguaSnapUpload';
import './LinguaSnaps.css';
import { FaPlus } from 'react-icons/fa';

const LinguaSnapsRow = ({ onRefresh }) => {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadFeed = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError(null);
      const { feed: data } = await getHomeFeed(50, true);
      console.log('[LinguaSnapsRow] Loaded feed:', data?.length, 'items', data);
      setFeed(data || []);
    } catch (err) {
      console.error('[LinguaSnapsRow] Error loading feed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
  }, [user?.id]);

  const handleOpenViewer = (index) => {
    setSelectedUserIndex(index);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  const handleUploadComplete = () => {
    setUploadOpen(false);
    loadFeed();
    onRefresh?.();
  };

  if (loading && feed.length === 0) {
    return (
      <div className="lingua-snaps-row">
        <div className="lingua-snaps-row-header">
          <h3>LinguaSnaps</h3>
        </div>
        <div className="lingua-snaps-row-content">
          <div className="lingua-snaps-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && feed.length === 0) {
    return null; // Hide the row if there's an error and no cached data
  }

  return (
    <>
      <div className="lingua-snaps-row">
        <div className="lingua-snaps-row-header">
          <div className="lingua-snaps-row-header-left">
            <h3>LinguaSnaps</h3>
            <span className="lingua-snaps-subtitle">12h stories from your connections</span>
          </div>
          <button
            className="lingua-snap-add-btn"
            onClick={() => setUploadOpen(true)}
            title="Create LinguaSnap"
          >
            <FaPlus /> New
          </button>
        </div>
        <div className="lingua-snaps-row-content">
          <button
            className="lingua-snap-create-tile snap-create-placeholder"
            onClick={() => setUploadOpen(true)}
            title="Create LinguaSnap"
            type="button"
          >
            <div className="snap-placeholder">
              <FaPlus />
              <span>Create</span>
            </div>
          </button>

          {/* User tiles */}
          {feed.map((item, index) => {
            const user = item.user || {};
            const snaps = item.snaps || [];
            const hasMultiple = snaps.length > 1;
            const latestSnap = snaps[0];
            const previewUrl = latestSnap?.thumbnailUrl || null;
            const timeLeft = latestSnap ? getTimeRemaining(latestSnap.expires_at) : null;
            const displayName = user.display_name || user.name || 'User';

            return (
              <button
                key={user.id || index}
                className="lingua-snap-tile"
                onClick={() => handleOpenViewer(index)}
                title={`${displayName}'s LinguaSnaps`}
              >
                <div className="lingua-snap-tile-media">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt=""
                      className="lingua-snap-preview"
                      loading="lazy"
                      decoding="async"
                    />
                  )}

                  <div className="lingua-snap-tile-overlay">
                    <div className="lingua-snap-tile-overlay-top">
                      <span className="lingua-snap-tile-name">{displayName}</span>
                    </div>
                    {timeLeft && (
                      <div className="lingua-snap-tile-overlay-bottom">
                        <span className="lingua-snap-tile-time">{timeLeft}</span>
                      </div>
                    )}
                  </div>

                  <div
                    className={`lingua-snap-ring ${hasMultiple ? 'multiple' : ''} ${previewUrl ? 'has-preview' : ''}`}
                  >
                    <img
                      src={user.avatar_url || '/avatar-placeholder.png'}
                      alt={displayName}
                      className="lingua-snap-avatar"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {hasMultiple && (
                    <span className="lingua-snap-count">{snaps.length}</span>
                  )}
                </div>
              </button>
            );
          })}

          {feed.length === 0 && !loading && (
            <div className="lingua-snaps-empty">
              No LinguaSnaps yet. Create one or connect with others!
            </div>
          )}
        </div>
      </div>

      {/* Viewer Modal */}
      {viewerOpen && feed.length > 0 && (
        <LinguaSnapViewer
          feed={feed}
          initialUserIndex={selectedUserIndex}
          onClose={handleCloseViewer}
        />
      )}

      {/* Upload Modal */}
      {uploadOpen && (
        <LinguaSnapUpload
          onClose={() => setUploadOpen(false)}
          onComplete={handleUploadComplete}
        />
      )}
    </>
  );
};

export default LinguaSnapsRow;
